import React, { useEffect, useState, useMemo } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardActions,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useAPI } from '../../../shared/services/api/API';
import { WASHUB_CONSTANTS } from '../../../shared/Constants';
import { ICategory, IProcess } from '../model/ProcessManagement';
const initialDateRange = {
  startDate: dayjs().subtract(5, 'days'),
  endDate: dayjs(),
};
const allOption = {
  id: 'ALL',
  name: 'ALL',
};
export const WashingReport = () => {
  const { axiosInstance, httpGet, httpPost, handleAlertBar } = useAPI();
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [category, setCategory] = useState('');
  const [program, setProgram] = useState('');
  const [categoryList, setCategoryList] = useState<ICategory[]>([]);
  const [programList, setProgramList] = useState<IProcess[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const queryParams = useMemo(
    () => ({
      page: 0,
      size: 1000,
      sortColumn: 'modifiedAt',
      isDescending: true,
    }),
    []
  );
  const fetchCategories = async () => {
    try {
      const res = await httpGet(WASHUB_CONSTANTS.API_URLS.CATEGORIES_DATA_GET, queryParams);
      if (res.status === 200) {
        setCategoryList([allOption, ...res.data]);
      }
    } catch (err) {
      handleAlertBar('error', 'Failed to load categories');
    }
  };
  const fetchPrograms = async () => {
    try {
      const res = await httpPost(WASHUB_CONSTANTS.API_URLS.PROCESS_TYPE_DATA_GET, null);
      if (res.status === 200) {
        setProgramList([allOption, ...res.data]);
      }
    } catch (err) {
      handleAlertBar('error', 'Failed to load programs');
    }
  };
  const handleReportDownload = async () => {
    setIsLoading(true);
    const payload = {
      startDate: dateRange.startDate.format('YYYY-MM-DD'),
      endDate: dateRange.endDate.format('YYYY-MM-DD'),
      category,
      program,
    };
    try {
      const response = await axiosInstance.post(
        WASHUB_CONSTANTS.API_URLS.GENERATE_DAILY_LAUNDRY_REPORT_DATA_POST,
        payload,
        {
          headers: {
            Authorization: localStorage.getItem('jwtToken') || '',
          },
          responseType: 'blob',
        }
      );
      if (response.status === 200) {
        const blob = new Blob([response.data], { type: 'application/xlsx' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute(
          'download',
          `daily-laundry-report-${payload.startDate} to ${payload.endDate}.xlsx`
        );
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      handleAlertBar('error', 'Failed to download report');
    } finally {
      setIsLoading(false);
    }
  };
  const renderSelect = (
    label: string,
    value: string,
    onChange: (val: string) => void,
    options: Array<{ id: string; name: string }>
  ) => (
    <TextField
      fullWidth
      select
      size="small"
      label={label}
      required
      variant="standard"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((option) => (
        <MenuItem key={option.name} value={option.name}>
          {option.name}
        </MenuItem>
      ))}
    </TextField>
  );
  useEffect(() => {
    fetchCategories();
    fetchPrograms();
  }, []);
  return (
    <div>
      <Grid container justifyContent="space-between">
        <Grid item className="mb-1 mt-1 ms-3">
          <Typography color="primary" variant="h5" textAlign="center">
            Washing Report
          </Typography>
        </Grid>
      </Grid>
      <Card sx={{ margin: '5px 15px' }} elevation={2}>
        <CardActions>
          <Grid container spacing={3} className="p-3">
          <Grid item xs={12} sm={6}  md={3} lg={3} className='mt-1'>
              {renderSelect('Select Category', category, setCategory, categoryList)}
            </Grid>
            <Grid item xs={12} sm={6}  md={3} lg={3} className='mt-1'>
              {renderSelect('Select Program', program, setProgram, programList)}
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.startDate}
                  onChange={(date) => {
                    if (date) {
                      setDateRange({ ...dateRange, startDate: date });
                    }
                  }}
                  slotProps={{ textField: { variant: 'standard' } }}
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}  md={3} lg={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="End Date"
                  value={dateRange.endDate}
                  minDate={dateRange.startDate}
                  onChange={(date) => {
                    if (date) {
                      setDateRange({ ...dateRange, endDate: date });
                    }
                  }}
                  slotProps={{ textField: { variant: 'standard' } }}
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="end">
              <Button
                variant="contained"
                onClick={handleReportDownload}
                disabled={category === '' || program === ''}
              >
                {isLoading ? 'Downloading...' : 'Download the Report'}
              </Button>
            </Grid>
          </Grid>
        </CardActions>
      </Card>
    </div>
  );
};
